import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthenticationGuard } from './core/auth/authentication.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { AdminRoleGuard } from 'app/core/auth/guards/admin-role.guard';
import { SupervisorRoleGuard } from 'app/core/auth/guards/supervisor-role.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { environment } from '../environments/environment';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to '/home'
  { path: '', pathMatch: 'full', redirectTo: 'home' },

  // Redirect signed-in user to the '/home'
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'home' },

  // Account activation / mandatory set-password. No guard: reachable by guests
  // (activation link) AND by signed-in users walled by must_change_password.
  {
    path: 'activate',
    component: LayoutComponent,
    data: { layout: 'empty' },
    loadChildren: () => import('app/modules/auth/activate/activate.routes'),
  },

  // Auth routes for guests
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      //{ path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes') },
      { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes') },
      { path: 'reset-password', redirectTo: 'forgot-password', pathMatch: 'full' },
      { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
      //{ path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') },
    ],
  },

  // Auth routes for authenticated users
  {
    path: '',
    canActivate: [AuthenticationGuard],
    canActivateChild: [AuthenticationGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
      //{ path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') },
    ],
  },

  // Firststart wizard — full screen, no sidebar/header
  {
    path: '',
    canActivate: [AuthenticationGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      { path: 'firststart', loadChildren: () => import('app/@firststart/firststart.routes') },
    ],
  },

  // Student routes
  {
    path: '',
    canActivate: [AuthenticationGuard],
    canActivateChild: [AuthenticationGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      { path: 'home', loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes') },
      { path: 'academy', loadChildren: () => import('app/modules/admin/academy/academy.routes') },
      { path: 'game-hub', loadChildren: () => import('app/modules/admin/game-hub/game-hub.routes').then(m => m.gameHubRoutes) },

      { path: 'notifications', loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes') },
      { path: 'profile', loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes') },

      // Support page - accessible to all authenticated users
      { path: 'support', loadChildren: () => import('app/modules/admin/support/support.routes') },
      { path: 'help', loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes') },

      // User settings - accessible to all authenticated users  
      { path: 'user-settings', loadChildren: () => import('app/modules/admin/settings/settings.routes') },

      { path: 'privacy', loadChildren: () => import('app/modules/admin/dashboard/dashboard.routes') },
    ],
  },

  // Supervisor routes - protected by SupervisorRoleGuard (only SuperUser, SysAdmin, CourseAdmin, Supervisor)
  {
    path: '',
    canActivate: [AuthenticationGuard, SupervisorRoleGuard],
    canActivateChild: [AuthenticationGuard, SupervisorRoleGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      { path: 'supervisor', loadChildren: () => import('app/modules/admin/chat/chat.routes') },
      { path: 'supervisor/classroom', loadChildren: () => import('app/modules/admin/supervisor-classroom/supervisor-classroom.routes') },
      { path: 'vclass-admin', loadChildren: () => import('app/modules/admin/classroom-admin/classroom-admin.routes') },
      // heisst supervisor: { path: 'chat', loadChildren: () => import('app/modules/admin/chat/chat.routes') },

      { path: 'students', loadChildren: () => import('app/modules/admin/contacts/contacts.routes') },
      { path: 'supervisors', loadChildren: () => import('app/modules/admin/supervisors/supervisors.routes') },
      { path: 'translators', loadChildren: () => import('app/modules/admin/translators/translators.routes').then(m => m.translatorsRoutes) },
      { path: 'companies', loadChildren: () => import('app/modules/admin/companies/companies.routes') },
      { path: 'courses', loadChildren: () => import('app/modules/admin/courses/course.routes') },

      // Certificates & Reports
      { path: 'certificates', loadChildren: () => import('app/modules/admin/certificates/certificates.routes').then(m => m.certificatesRoutes) },
      { path: 'reports', loadChildren: () => import('app/modules/admin/reports/reports.routes').then((m: any) => m.reportsRoutes) },

      // LMS Module Management
      { path: 'modules-mgmt', loadChildren: () => import('app/modules/admin/modules-mgmt/modules-mgmt.routes').then(m => m.modulesMgmtRoutes) },

      // LMS Badges Management
      { path: 'badges-mgmt', loadChildren: () => import('app/modules/admin/badges-mgmt/badges-mgmt.routes').then(m => m.badgesMgmtRoutes) },

      // LMS Student Groups
      { path: 'groups-mgmt', loadChildren: () => import('app/modules/admin/groups-mgmt/groups-mgmt.routes').then(m => m.groupsMgmtRoutes) },

      // LMS Certificate Templates
      { path: 'cert-templates', loadChildren: () => import('app/modules/admin/cert-templates/cert-templates.routes').then(m => m.certTemplatesRoutes) },

      // Website Sales (orders / revenue / subscriptions)
      { path: 'sales', loadChildren: () => import('app/modules/admin/sales/sales.routes').then(m => m.salesRoutes) },

      // LMS Study Plans
      { path: 'study-plans', loadChildren: () => import('app/modules/admin/study-plans/study-plans.routes').then(m => m.studyPlansRoutes) },

      // LMS Study Matrix
      { path: 'study-matrix', loadChildren: () => import('app/modules/admin/study-matrix/study-matrix.routes').then(m => m.studyMatrixRoutes) },

      // Quiz System (list) + Quiz Editor (opened from within the widget)
      { path: 'quiz-system', loadChildren: () => import('app/modules/admin/quiz-system/quiz-system.routes').then(m => m.quizSystemRoutes) },
      { path: 'quiz-editor/:id', loadChildren: () => import('app/modules/admin/quiz-system/quiz-editor-host.routes').then(m => m.quizEditorHostRoutes) },

      // Settings - User & Role Management (Admin only)
      { path: 'settings', loadChildren: () => import('app/modules/admin/settings/settings.routes') },

      // System Settings - Email & Supervisor Templates (Admin only)
      { path: 'system-settings', loadChildren: () => import('app/modules/admin/system-settings/system-settings.routes') },

      // System - Server diagnostics & debug controls (SuperUser/SysAdmin only)
      { path: 'system', loadChildren: () => import('app/modules/admin/system/system.routes') },
    ],
  },

  // Wildcard Route: Redirects all unknown paths to a 404 page or home
  { path: '**', redirectTo: 'home', pathMatch: 'full' },
];
